import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import Store from './storeSymbol';
import EventManager from './eventManager';
import validate from './schema/validate';
import { isSchema } from './schema';
import genericInternalStateSetter from './internalStateSetter';

const emptyFunc = () => null;

function IsListOfError(message = '') {
  this.name = 'IsListOfError';
  this.message = message;
}
IsListOfError.prototype = Error.prototype;

function InvalidContainerError(message = '') {
  this.name = 'InvalidContainerError';
  this.message = message;
}
InvalidContainerError.prototype = Error.prototype;

const processIsListOf = (isListOf, state, presetSingularizedValue) => {
  let items;
  let singularizedValue;
  switch (({}).toString.call(isListOf)) {
    case '[object String]':
      const parts = isListOf.split('.');
      items = parts.reduce((acc, el) => {
        if (!acc || acc.length === 0) return [];
        if (acc.hasOwnProperty(el)) return acc[el];
        throw new IsListOfError(`Bad isListOf path: ${isListOf}(${el})`);
      }, state);
      singularizedValue = pluralize.singular(parts.pop());
      break;
    case '[object Array]':
      items = isListOf;
      singularizedValue = '_listItem';
      break;
    case '[object Function]':
      items = isListOf(state);
      singularizedValue = '_listItem';
      break;
    case '[object Object]':
      return processIsListOf(Object.values(isListOf)[0], state, Object.keys(isListOf)[0]);
    default:
      throw new IsListOfError(`Bad isListOf Value: ${isListOf}`);
  }
  return { items, singularizedValue: presetSingularizedValue || singularizedValue };
};

const renderElement = (elementDef, state, parentComponents, controller) => {
  if (elementDef.isListOf) {
    const { items, singularizedValue } = processIsListOf(elementDef.isListOf, state);

    items?.forEach((item, index) => {
      const newState = { ...state, [singularizedValue]: item };
      newState[`${singularizedValue}Index`] = index;
      const newDef = { ...elementDef, isListOf: false };
      newDef.key = elementDef.key ? `${elementDef.name}.${elementDef.key(newState)}` : `${elementDef.name}.${index}`;
      renderElement(newDef, newState, parentComponents, controller);
    });
  } else if (!elementDef.renderIf || elementDef.renderIf(state)) {
    if (elementDef.wrapper || elementDef.wrapperSelect) {
      const childComponents = [];
      if (!elementDef.elements) throw `${elementDef.name} requires 'elements' property`;
      elementDef.elements.forEach((el) => {
        const newName = el.name === '__transparent__' ? elementDef.name : `${elementDef.name}.${el.name}`;
        const newDef = { ...el, name: newName, eventName: newName };
        renderElement(newDef, state, childComponents, controller);
      });
      const wrapperAttrs = {
        childElement: childComponents,
        key: elementDef.key || elementDef.name
      };
      if (elementDef.ref) {
        if (typeof elementDef.ref === 'function') {
          wrapperAttrs._innerRef = elementDef.ref(state);
        } else {
          wrapperAttrs._innerRef = elementDef.ref;
        }
      }
      if (elementDef.displayState && (typeof elementDef.displayState !== 'function')) throw `${elementDef.name} has an invalid displayState`;
      if (elementDef.displayState) wrapperAttrs.displayState = elementDef.displayState(state);
      if (elementDef.colSpan) wrapperAttrs.colSpan = elementDef.colSpan;
      if (elementDef.isButton) {
        const handler = controller.bind(null, elementDef.eventName, (elementDef.bindings || emptyFunc)(state));
        wrapperAttrs.onClick = (e) => { e.stopPropagation(); handler(); }
      }
      const Wrapper = elementDef.wrapperSelect ? elementDef.wrapperSelect((elementDef.bindings || emptyFunc)(state)) : elementDef.wrapper;
      const element = <Wrapper {...wrapperAttrs} />;
      parentComponents.push(element);
    } else {
      if (!elementDef.component && !elementDef.componentSelect) {
        throw new InvalidContainerError(`${elementDef.name} requires 'component', 'componentSelect' or 'wrapper' property`);
      }
      if (elementDef.displayState && (typeof elementDef.displayState !== 'function')) {
        throw new InvalidContainerError(`${elementDef.name} has an invalid displayState`);
      }
      if (elementDef.displayValue && (typeof elementDef.displayValue !== 'function')) {
        throw new InvalidContainerError(`${elementDef.name} has an invalid displayValue`);
      }
      if (elementDef.bindings && (typeof elementDef.bindings !== 'function')) {
        throw new InvalidContainerError(`${elementDef.name} has invalid bindings`);
      }
      const elementState = {
        name: elementDef.name,
        key: elementDef.key || elementDef.name,
        displayState: (elementDef.displayState || emptyFunc)(state),
        displayValue: (elementDef.displayValue || emptyFunc)(state),
        eventManager: controller,
        eventName: elementDef.eventName,
        bindings: (elementDef.bindings || emptyFunc)(state)
      };
      if (elementDef.makeRef) {
        elementState.ref = React.createRef();
        elementState._innerRef = elementState.ref;
        elementState.bindings._elementRef = elementState.ref;
      }
      const { component, componentSelect } = elementDef;
      if (elementDef.ref) elementState.ref = elementDef.ref;
      const Element = componentSelect ? componentSelect(elementState.bindings) : component;
      const el = <Element {...elementState} key={elementState.key} />;
      parentComponents.push(el);
    }
  }
};

const Container = ({ name: containerName, props: containerProps, elements, renderIf }) => {
  if (!containerName) throw new InvalidContainerError('container requires name');
  const globalStateFields = [];
  const internalState = {};
  const internalStateSchemas = {};

  for (const field in containerProps) {
    if (containerProps[field] == Store) {
      globalStateFields.push(field);
    } else {
      const value = containerProps[field];
      if (isSchema(value)) {
        internalStateSchemas[field] = value;
        internalState[field] = value.defaultValue;
      } else if (value?.schema) {
        internalStateSchemas[field] = value.schema;
        internalState[field] = value.schema.defaultValue || value.defaultValue;
      } else {
        internalState[field] = value;
      }
    }
  }

  const mapStateToProps = (globalState) => {
    const ret = {};
    globalStateFields.forEach((f) => ret[f] = globalState[f]);
    return ret;
  };

  const base = (props) => {
    const { stateManager, controller, __overrides__ } = props;
    Object.keys(__overrides__ || {}).forEach((k) => {
      if (internalStateSchemas[k]) validate(`${containerName}:${k}`, internalStateSchemas[k], __overrides__[k]);
    });

    const [iState, setInternalState] = useState({ ...internalState, ...__overrides__ });

    const internalStateSetter = genericInternalStateSetter.bind(null, iState, internalStateSchemas, containerName, setInternalState);

    const fullState = { ...props, ...iState };
    delete fullState.dispatch;
    delete fullState.stateManager;
    delete fullState.controller;

    if (controller.init) {
      useEffect(() => {
        if (renderIf && !renderIf(fullState)) return;
        controller.init(fullState, stateManager, internalStateSetter);
      }, [renderIf, ...(controller.refreshOn || (() => []))(fullState)]);
    }
    if (renderIf && !renderIf(fullState)) return null;

    const moleculeComponents = [];
    const wrappedManager = EventManager(controller, stateManager, internalStateSetter);

    for (const elementDef of elements) {
      if (!elementDef.name) throw new InvalidContainerError(`container ${containerName} contains nameless element`);
      const name = elementDef.name === '__transparent__' ? containerName : `${containerName}.${elementDef.name}`;
      const newDef = { ...elementDef, name, eventName: name };
      renderElement(newDef, fullState, moleculeComponents, wrappedManager);
    }
    return moleculeComponents;
  };

  return connect(mapStateToProps)(base);
};

export default Container;
